import { DateTime } from "luxon";
import type { GetBookingFloorParams } from "../../Booking-Form/thunks/booking.thunks";
import { BookingType, Frequence } from "../../Booking-Form/typings/booking-inputs";
import type { PlaceBookingDto } from "../../Booking-Form/typings/booking.types";
import { defaultRecurrenceWeekdays } from "../../Booking-Form/typings/shared/weekdays";
import type { TeamMember } from "../../Booking-Form/typings/team-member";
import type { ConnectedBookingData } from "../../Booking-Form/typings/connected-bookingData";
import type { FloorAvailability } from "../../FloorManager/typings/floor-inventory.entity";
import type { ISeatStatus } from "../../../components/FacilityManager/Domain/Types/FloorPlan/SeatStatus.type";
import { getPlanDateRangeOrNull } from "../../Booking-Form/functions/floor.functions";
import type { UserInformation } from "../../Login/typings/login.types";
import {
  generateInitial,
  genInitialByColleaguesBooking
} from "../../../components/FacilityManager/Components/Views/SelectFloorPlanView/Functions/SelectFloorPlanView.functions";
import type { IFloorPayload } from "../../../components/FacilityManager/Domain/Types/FloorPlan/FloorPayload.type";
import {
  ColleagueDeskClickKind,
  type ColleagueFloorChip,
  type ColleagueWorkplaceFloorSession
} from "./colleague-workplace-floor.types";

/**
 * Identity of the outline currently drawn so a floor switch can drop a stale Pixi scene.
 * Used by the colleague workplace dialog when chips change floor.
 */
export function colleagueFloorSceneKey(
  floorPlan?: Pick<IFloorPayload, "floorInventoryId" | "outlineUrl">
): string {
  if (floorPlan == null) {
    return "";
  }
  return `${floorPlan.floorInventoryId}:${floorPlan.outlineUrl ?? ""}`;
}

export const emptyColleagueWorkplaceFloorSession: ColleagueWorkplaceFloorSession = {
  open: false,
  date: "",
  startTime: "08:00",
  endTime: "17:00"
};

/** Floor availability request for workplaces on one site and day. */
export function buildColleagueFloorAvailabilityArgs(args: {
  locationInventoryId: number;
  user: Pick<TeamMember, "userId" | "firstName" | "surname">;
  date: string;
  startTime: string;
  endTime: string;
}): GetBookingFloorParams {
  return {
    users: [
      {
        userId: args.user.userId,
        firstName: args.user.firstName,
        surname: args.user.surname,
        isExternal: false
      }
    ],
    startDate: args.date,
    endDate: args.date,
    startTime: args.startTime,
    endTime: args.endTime,
    bookingType: BookingType.WORKPLACE,
    locationInventoryId: args.locationInventoryId,
    description: ""
  };
}

export function colleagueFloorPlanIsoRange(
  date: string,
  startTime: string,
  endTime: string
): { start: string; end: string } | null {
  return getPlanDateRangeOrNull(date, startTime, date, endTime);
}

/** Workplace inventory name for occupancy-table title (`inventory.name`, not uid). */
export function colleagueFloorInventoryName(
  floorPlan: Pick<IFloorPayload, "places"> | undefined,
  inventoryId: number | undefined
): string | undefined {
  if (inventoryId == null) {
    return undefined;
  }
  const place = floorPlan?.places.find(
    workplace => workplace.inventoryId === inventoryId || workplace.inventory?.id === inventoryId
  );
  const name = place?.inventory?.name?.trim();
  return name === "" ? undefined : name;
}

/**
 * Query window for GET /booking-schedule/place from a colleague-floor occupancy click.
 * Date-only `endDate` is start-of-day UTC (`LessThanOrEqual`), so the selected day must
 * be sent as the following calendar day — same convention as Dashboard2 week schedules.
 */
export function colleagueFloorScheduleQueryRange(
  date: string
): { startDate: string; endDate: string } | null {
  const day = DateTime.fromISO(date);
  if (!day.isValid) {
    return null;
  }
  return {
    startDate: day.toFormat("yyyy-MM-dd"),
    endDate: day.plus({ days: 1 }).toFormat("yyyy-MM-dd")
  };
}

/** Chip label data: floor name + how many colleagues booked on that floor. */
export function toColleagueFloorChips(floors: FloorAvailability[]): ColleagueFloorChip[] {
  return floors.map(floor => ({
    id: floor.id,
    name: floor.name,
    colleagueCount: floor.connectedBookingData?.length ?? 0
  }));
}

export function pickDefaultColleagueFloorId(
  floors: FloorAvailability[],
  highlightInventoryId?: number,
  preferredFloorId?: number
): number | undefined {
  if (highlightInventoryId != null) {
    const withHighlight = floors.find(floor => {
      const occupied = new Set([
        ...(floor.occupiedInventoryIds ?? []),
        ...(floor.userOccupiedInventoryIds ?? []),
        ...(floor.availableInventoryIds ?? [])
      ]);
      const connected = floor.connectedBookingData?.some(
        row => row.bookingInventoryId === highlightInventoryId
      );
      return occupied.has(highlightInventoryId) || Boolean(connected);
    });
    if (withHighlight) {
      return withHighlight.id;
    }
  }

  if (preferredFloorId != null && floors.some(floor => floor.id === preferredFloorId)) {
    return preferredFloorId;
  }

  return floors[0]?.id;
}

/**
 * Seat lists for the colleague dialog. Partial desks are painted as pies.
 * Wizard occupancy stays binary because it does not set `partialList`.
 */
export function buildColleagueWorkplaceSeatStatus(currentFloor: FloorAvailability): ISeatStatus {
  const partialIds = currentFloor.partiallyOccupiedInventoryIds ?? [];
  const partialSet = new Set(partialIds);
  const blocked = new Set(currentFloor.blockedInventoryIds ?? []);

  return {
    availableList: currentFloor.availableInventoryIds.filter(id => !blocked.has(id)),
    occupiedList: currentFloor.occupiedInventoryIds,
    disableList: currentFloor.disabledInventoryIds,
    restrictedList: currentFloor.forbiddenInventoryIds,
    userOccupiedList: currentFloor.userOccupiedInventoryIds ?? [],
    blockedList: [...blocked],
    partialList: [...partialSet]
  };
}

export function isColleagueDeskBookable(
  inventoryId: number | undefined,
  seatStatus: ISeatStatus
): boolean {
  return resolveColleagueDeskClickKind(inventoryId, seatStatus) === ColleagueDeskClickKind.BOOK;
}

/**
 * Free desks book; occupied and partial desks open calendar occupancy instead.
 */
export function resolveColleagueDeskClickKind(
  inventoryId: number | undefined,
  seatStatus: ISeatStatus
): ColleagueDeskClickKind {
  if (inventoryId == null) {
    return ColleagueDeskClickKind.NONE;
  }

  const available = new Set(seatStatus.availableList);
  const partial = new Set(seatStatus.partialList ?? []);
  const occupied = new Set(seatStatus.occupiedList);
  const forbidden = new Set(seatStatus.restrictedList);
  const disabled = new Set(seatStatus.disableList);
  const blocked = new Set(seatStatus.blockedList ?? []);

  if (forbidden.has(inventoryId) || disabled.has(inventoryId) || blocked.has(inventoryId)) {
    return ColleagueDeskClickKind.NONE;
  }

  if (partial.has(inventoryId) || (occupied.has(inventoryId) && !available.has(inventoryId))) {
    return ColleagueDeskClickKind.OCCUPANCY;
  }

  if (available.has(inventoryId)) {
    return ColleagueDeskClickKind.BOOK;
  }

  return ColleagueDeskClickKind.NONE;
}

/** Occupancy rows for one desk, including the login user when the API marks the seat as theirs. */
export function occupancyBookingsForDesk(args: {
  inventoryId: number;
  connectedBookingData: ConnectedBookingData[];
  currentUser: Pick<ConnectedBookingData, "userId" | "firstName" | "lastName" | "email">;
  isUserOccupied: boolean;
}): ConnectedBookingData[] {
  const bookings = args.connectedBookingData.filter(
    row => row.bookingInventoryId === args.inventoryId
  );
  if (args.isUserOccupied && !bookings.some(row => row.userId === args.currentUser.userId)) {
    return [
      ...bookings,
      {
        userId: args.currentUser.userId,
        firstName: args.currentUser.firstName,
        lastName: args.currentUser.lastName,
        email: args.currentUser.email,
        bookingInventoryId: args.inventoryId
      }
    ];
  }
  return bookings;
}

/** True when several occupancy rows exist — partial vs full seat paint does not matter. */
export function shouldOpenColleagueOccupancyTable(bookings: ConnectedBookingData[]): boolean {
  return bookings.length > 1;
}

export function formatColleagueOccupancyTimeRange(
  row: Pick<ConnectedBookingData, "start" | "end">,
  timeFormat: string
): string {
  if (!row.start || !row.end) {
    return "";
  }
  const start = DateTime.fromISO(row.start);
  const end = DateTime.fromISO(row.end);
  if (!start.isValid || !end.isValid) {
    return "";
  }
  return `${start.toFormat(timeFormat)}–${end.toFormat(timeFormat)}`;
}

/** True when the login user occupies this desk for the current availability window (API only). */
export function isColleagueUserOccupiedDesk(
  inventoryId: number | undefined,
  seatStatus: ISeatStatus
): boolean {
  if (inventoryId == null) {
    return false;
  }

  return seatStatus.userOccupiedList?.includes(inventoryId) ?? false;
}

/**
 * Session highlight is valid only when the logged-in user is on that desk in the
 * current floor availability response (any floor at the site).
 */
export function isColleagueSessionHighlightValid(
  floors: FloorAvailability[],
  highlightInventoryId: number | undefined,
  userId: string
): boolean {
  if (highlightInventoryId == null || floors.length === 0) {
    return false;
  }

  return floors.some(floor => {
    if (floor.userOccupiedInventoryIds?.includes(highlightInventoryId)) {
      return true;
    }

    return (
      floor.connectedBookingData?.some(
        row => row.userId === userId && row.bookingInventoryId === highlightInventoryId
      ) ?? false
    );
  });
}

export function toHighlightedColleagueUserBookings(
  usersBookedFor: TeamMember[],
  highlightInventoryId?: number
): TeamMember[] {
  const [currentUser] = usersBookedFor;
  if (currentUser == null || highlightInventoryId == null) {
    return usersBookedFor;
  }

  return [{ ...currentUser, bookingInventoryId: highlightInventoryId }];
}

export function generateColleagueFloorIcon(args: {
  highlightedUserBookings: TeamMember[];
  userInformation: UserInformation | undefined;
  searchedColleagues: ConnectedBookingData[];
  inventoryId: number | undefined;
  isUserOccupiedPlace: boolean;
}): string | undefined {
  if (args.inventoryId == null) {
    return undefined;
  }

  const colleaguesOnDesk = args.searchedColleagues.filter(
    colleague => colleague.bookingInventoryId === args.inventoryId
  );
  if (colleaguesOnDesk.length > 0) {
    return genInitialByColleaguesBooking(colleaguesOnDesk);
  }

  return generateInitial(
    "booking",
    args.highlightedUserBookings,
    args.userInformation,
    [],
    args.inventoryId,
    args.isUserOccupiedPlace
  );
}

/**
 * True when PlaceIcon can paint the string: two-letter initials, a short "+N" stack, or a fetchable image URL.
 * Profile `photoUrl` is often a storage key (`user-photos/…`). That is longer than a label and is not a URL, so PlaceIcon loads nothing and draws nothing.
 */
export function isDrawableColleagueFloorIcon(icon: string): boolean {
  if (icon.length === 2) {
    return true;
  }
  if (icon.length >= 4 && icon.length <= 6 && icon.includes("+")) {
    return true;
  }
  return icon.startsWith("http://") || icon.startsWith("https://") || icon.startsWith("data:");
}

function colleagueFloorLoginInitials(
  firstName: string | undefined,
  surname: string | undefined
): string | undefined {
  const first = firstName?.trim();
  const last = surname?.trim();
  if (!first || !last) {
    return undefined;
  }
  return `${first[0]}${last[0]}`.toUpperCase();
}

function isUserPhotoStorageKey(value: string): boolean {
  const trimmed = value.trim();
  return trimmed.startsWith("user-photos/") || trimmed.startsWith("/user-photos/");
}

/** Public object URL: `REACT_APP_BUCKET_URL` + storage key. Empty when the bucket env is unset. */
function toBucketPhotoUrl(storageKey: string): string | undefined {
  const bucketBase = process.env.REACT_APP_BUCKET_URL?.replace(/\/+$/, "");
  if (!bucketBase) {
    return undefined;
  }
  const key = storageKey.replace(/^\/+/, "");
  return `${bucketBase}/${key}`;
}

/**
 * Icon for one colleague-floor desk. Keeps initials and real image URLs.
 * Turns the logged-in user's storage-key photo into a bucket URL so Pixi can load it.
 * Initials are the fallback when `REACT_APP_BUCKET_URL` is unset.
 */
export function toDrawableColleagueFloorIcon(
  generated: string | undefined,
  loginUser: { firstName?: string; surname?: string; photoUrl?: string | null }
): string | undefined {
  if (!generated) {
    return undefined;
  }
  if (isDrawableColleagueFloorIcon(generated)) {
    return generated;
  }
  if (isUserPhotoStorageKey(generated)) {
    const bucketUrl = toBucketPhotoUrl(generated);
    if (bucketUrl) {
      return bucketUrl;
    }
    if (loginUser.photoUrl && generated === loginUser.photoUrl) {
      return colleagueFloorLoginInitials(loginUser.firstName, loginUser.surname);
    }
    return undefined;
  }
  if (loginUser.photoUrl && generated === loginUser.photoUrl) {
    return (
      toBucketPhotoUrl(generated) ??
      colleagueFloorLoginInitials(loginUser.firstName, loginUser.surname)
    );
  }
  return undefined;
}

export function colleagueSearchLabel(row: ConnectedBookingData): string {
  const name = `${row.firstName} ${row.lastName}`.trim();
  return name === "" ? row.email : name;
}

/** Normalize Mobiscroll datepicker values to `yyyy-MM-dd` for the colleague floor session. */
export function toColleagueFloorDateInputValue(value: unknown): string | null {
  if (value instanceof Date) {
    return DateTime.fromJSDate(value).toFormat("yyyy-MM-dd");
  }
  if (typeof value === "string") {
    const parsed = DateTime.fromISO(value);
    return parsed.isValid ? parsed.toFormat("yyyy-MM-dd") : null;
  }
  return null;
}

/** Normalize Mobiscroll timepicker values to `HH:mm` for the colleague floor session. */
export function toColleagueFloorTimeInputValue(value: unknown): string | null {
  if (value instanceof Date) {
    return DateTime.fromJSDate(value).toFormat("HH:mm");
  }
  if (typeof value === "string") {
    const directTime = value.match(/^\d{2}:\d{2}/)?.[0];
    if (directTime) {
      return directTime;
    }
    const parsed = DateTime.fromISO(value);
    return parsed.isValid ? parsed.toFormat("HH:mm") : null;
  }
  return null;
}

/** One hover-tooltip row for a desk occupancy booking (name + time). */
export type ColleagueDeskTooltipLine = {
  id: string;
  name: string;
  time: string;
};

/** Lines for the desk hover tooltip; empty when the desk has no occupancy rows. */
export function buildColleagueDeskTooltipLines(
  bookings: ConnectedBookingData[],
  timeFormat: string,
  unknownTimeLabel: string
): ColleagueDeskTooltipLine[] {
  return bookings.map(booking => {
    const timeLabel = formatColleagueOccupancyTimeRange(booking, timeFormat);
    return {
      id: `${booking.userId}-${booking.bookingId ?? `${booking.start ?? ""}-${booking.end ?? ""}`}`,
      name: colleagueSearchLabel(booking),
      time: timeLabel === "" ? unknownTimeLabel : timeLabel
    };
  });
}

export function buildColleaguePlaceBookingDto(args: {
  user: Pick<TeamMember, "userId" | "firstName" | "surname">;
  locationInventoryId: number;
  inventoryId: number;
  date: string;
  startTime: string;
  endTime: string;
}): PlaceBookingDto {
  return {
    users: [
      {
        userId: args.user.userId,
        firstName: args.user.firstName,
        surname: args.user.surname,
        bookingInventoryId: args.inventoryId,
        isExternal: false
      }
    ],
    bookingType: BookingType.WORKPLACE,
    locationInventoryId: args.locationInventoryId,
    automatedSeating: false,
    automatedSeatingObjects: [],
    weekdays: defaultRecurrenceWeekdays,
    frequence: Frequence.SINGLE,
    startDate: args.date,
    endDate: args.date,
    startTime: args.startTime,
    endTime: args.endTime
  };
}
